/*
 * Copyright, AlpineReplay, Inc. 2021. All rights reserved.
 * Author: Brian Bal
 */
import { connect } from "react-redux"
import React, { useState, useRef, useEffect } from "react"
import { Color, Element, Label, trackEvent, TraceTeamInfoCard } from "trux"
import PlayerStatsRow from "./PlayerStatsRow.js"
import { PanelTitle } from "../../components/PanelTitle"
import { SensorlessResultsContent } from "../../components/MissingSensors"
import * as links from "../../util/links"
import { calculateBaseballStats as _calculateBaseballStats, getShouldShowTraceTeam } from "../../util/helpers"
import { formatFirstName } from "../../trace/formatters"
import "./PlayersPanel.css"

function PlayersPanelComponent (props) {
    const [allDummies, setAllDummies] = useState(true)
    const [sortState, setSortState] = useState({
        column: 'name',
        order: 1,
        func: nameSortFunc
    })

    const scrollRef = useRef()

    function closePressed() {
        let noPanelUrl = links.gameUrl(
            props.teamName,
            props.playlistId,
            props.gameId,
            props.eventNumber
        )
        props.history.push(noPanelUrl)
    }

    function filterHalves(selectedHalf) {
        props.selectHalf(selectedHalf)
    }

    function onPlayerPressed(e, traceNumber) {
        var playerUrl = links.gamePlayerDetailsUrl(
            props.teamName,
            props.playlistId,
            props.gameId,
            props.eventNumber,
            traceNumber
        )

        props.history.push(playerUrl)
    }

    useEffect(() => {
        if (scrollRef.current) {
            const y = scrollRef.current.getBoundingClientRect().top - 124
            window.scrollTo({ top: y, behavior: "auto" })
        }
        trackEvent({
            action: "Players Panel Opened",
            element: document.querySelector(".PlayersPanel"),
        })
    }, [])

    function nameSortFunc(a, b) {
        if (!a || !b) return
            
        if (a.athlete.unknown !== b.athlete.unknown) {
            return (a.athlete.unknown ? 1 : -1) * sortState.order
        }
        if (a.athlete.unknown) {
            return (
                (parseInt(a.trace_number, 10) - parseInt(b.trace_number, 10)) *
                sortState.order
            )
        }
        return (
            a.athlete.user.abbr.localeCompare(b.athlete.user.abbr) *
            sortState.order
        )
    }

    function setSort(column) {
        const order =
            sortState.column === column
                ? sortState.order * -1
                : column === "name"
                ? 1
                : -1
        const sortfunc =
            column === "name"
                ? nameSortFunc
                : (a, b) => {
                    if (!a || !b) return

                    return (
                        sortState.order *
                        (a[sortState.column] - b[sortState.column])
                    )
                  }

        setSortState({
            order: order,
            column: column,
            func: sortfunc
        })   
    }

    function getSortClass(column) {
        if (sortState.column === column) {
            return sortState.order === 1 ? "sorted asc" : "sorted desc"
        }
        return ""
    }

    function calculateBaseballStats(inning = 'all-innings') {
        let stats = []
        if(props.game) {
            props.setBaseballStats({ loading: true, complete: false, error: null })
            stats = _calculateBaseballStats(props.game, inning)
        }
        props.setBaseballStats({ value: stats, loading: false, complete: true, error: null })
    }

    function handleShowTraceTeamDialog() {
        props.history.push(props.history.location.pathname + "/upgrade-trace-team")
    }

    // Render
    let noPanelUrl = links.gameUrl(
        props.teamName,
        props.playlistId,
        props.gameId,
        props.eventNumber
    )

    if (props.loading) {
        return (
            <Element className="PlayersPanel">
                <Element
                    className="side-panel-backdrop"
                    onClick={(e) => props.history.push(noPanelUrl)}
                />
                <Element className="side-panel-buddy" />
                <Element className="players-panel side-panel extended-panel-title">
                    <Element className="side-panel-content">
                        <Label className="loading">
                            <i className="trace-icon loading-ball-gray" />
                            <span>Loading &hellip;</span>
                        </Label>
                    </Element>
                </Element>
            </Element>
        )
    } else {
        let sportType = props.sportType

        let game = props.game
        let user = props.user
        let userDivisions = props.userDivisions
        let isTraceFamilyGame =
            game && game.division?.type === "family" ? true : false
        let showTraceTeamInfoCard = getShouldShowTraceTeam(
            game,
            user,
            userDivisions
        )

        let statistics =
            sportType === "soccer"
                ? props.playerStats
                : props.playerStatsBaseball
        if (isTraceFamilyGame) {
            statistics = statistics.slice(0, 3)
        }

        // Sensorless logic
        const isSensorless = props.renderType === "sensorless"
        const isPanoramicSensorless = props.renderType === "panoramic_sensorless_global_identity"
        const sensorlessGameClass =
            isSensorless && isTraceFamilyGame
                ? "sensorless-game trace-family"
                : "sensorless-game"
        const sensorlessGameTitle =
            isSensorless && isTraceFamilyGame
                ? "Could not identify players"
                : "Not enough Tracers to identify players ☹️"

        let playerStatRows = statistics.sort(sortState.func).map((stat) => {
            const firstName = formatFirstName(stat.athlete)
            const isDummy = /Tracer/gi.test(firstName)
            if (stat.no_data && isDummy) {
                return null
            } else {
                if (allDummies) setAllDummies(false)
                return (
                    <PlayerStatsRow
                        key={stat.athlete.id}
                        playerStats={stat}
                        teamName={props.teamName}
                        gameId={props.gameId}
                        eventNumber={props.eventNumber}
                        playlistId={props.playlistId}
                        onPlayerPressed={onPlayerPressed}
                        playerCount={statistics.length}
                        history={props.history}
                        sportType={sportType}
                        showStats={props.showStats}
                        isTraceFamilyGame={isTraceFamilyGame}
                        isPanoramicSensorlessGame={isPanoramicSensorless}
                    />
                )
            }
        })

        const sidePanelContentCn = isPanoramicSensorless ? "side-panel-content no-filters" : 'side-panel-content'

        return (
            <Element data-trk-ctx="Players Panel" className="PlayersPanel">
                <Element
                    className="side-panel-backdrop"
                    onClick={(e) => props.history.push(noPanelUrl)}
                />
                <Element className="side-panel-buddy" />
                <Element className="players-panel side-panel extended-panel-title">
                    <PanelTitle
                        onClosePressed={closePressed}
                        title="Players"
                        hideFilters={isPanoramicSensorless}
                        filterHalves={filterHalves}
                        filterInnings={(inning) =>
                            calculateBaseballStats(inning)
                        }
                    />
                    <Element className={sidePanelContentCn}>
                        {!isPanoramicSensorless && (
                            <Element className="tr table-header">
                                <div ref={scrollRef} />
                                <Element
                                    data-trk-act="Sort by Name"
                                    className={
                                        "td avatar " + getSortClass("avatar")
                                    }
                                    onClick={(e) => {
                                        setSort("name")
                                    }}
                                >
                                    &nbsp;
                                </Element>
                                <Element
                                    data-trk-act="Sort by Name"
                                    className={
                                        "td name hide-small" +
                                        getSortClass("name")
                                    }
                                    onClick={(e) => {
                                        setSort("name")
                                    }}
                                >
                                    &nbsp;
                                </Element>
                                {sportType === "soccer" && (
                                    <Element
                                        data-trk-act="Sort by Effort"
                                        className={
                                            "td stat stat-effort " +
                                            getSortClass("effort")
                                        }
                                        onClick={(e) => {
                                            setSort("effort")
                                        }}
                                    >
                                        <Label type={Label.Type.Title5}>
                                            Max Efforts
                                        </Label>
                                    </Element>
                                )}
                                {sportType === "baseball" && (
                                    <Element
                                        data-trk-act="Sort by Reached Safely"
                                        className={
                                            "td stat stat-reached-safely " +
                                            getSortClass("reachedSafely")
                                        }
                                        onClick={(e) => {
                                            setSort("reachedSafely")
                                        }}
                                    >
                                        <Label type={Label.Type.Title5}>
                                            Reached Safely
                                        </Label>
                                    </Element>
                                )}
                                {sportType === "soccer" && (
                                    <Element
                                        data-trk-act="Sort by Stamina"
                                        className={
                                            "td stat stat-stamina " +
                                            getSortClass("stamina")
                                        }
                                        onClick={(e) => {
                                            setSort("stamina")
                                        }}
                                    >
                                        <Label type={Label.Type.Title5}>
                                            Stamina
                                        </Label>
                                    </Element>
                                )}
                                {sportType === "baseball" && (
                                    <Element
                                        data-trk-act="Sort by At Bats"
                                        className={
                                            "td stat stat-at-bat " +
                                            getSortClass("atBat")
                                        }
                                        onClick={(e) => {
                                            setSort("atBat")
                                        }}
                                    >
                                        <Label type={Label.Type.Title5}>
                                            At Bats
                                        </Label>
                                    </Element>
                                )}
                                {sportType === "soccer" && (
                                    <Element
                                        data-trk-act="Sort by Distance"
                                        className={
                                            "td stat stat-distance " +
                                            getSortClass("distance")
                                        }
                                        onClick={(e) => {
                                            setSort("distance")
                                        }}
                                    >
                                        <Label type={Label.Type.Title5}>
                                            Distance (Miles)
                                        </Label>
                                    </Element>
                                )}
                                {sportType === "baseball" && (
                                    <Element
                                        data-trk-act="Sort by Defensive Plays"
                                        className={
                                            "td stat stat-defensive-play " +
                                            getSortClass("defensivePlay")
                                        }
                                        onClick={(e) => {
                                            setSort("defensivePlay")
                                        }}
                                    >
                                        <Label type={Label.Type.Title5}>
                                            Defensive Plays
                                        </Label>
                                    </Element>
                                )}
                                {sportType === "soccer" && (
                                    <Element
                                        data-trk-act="Sort by Speed"
                                        className={
                                            "td stat stat-speed " +
                                            getSortClass("top_speed")
                                        }
                                        onClick={(e) => {
                                            setSort("top_speed")
                                        }}
                                    >
                                        <Label type={Label.Type.Title5}>
                                            Top Speed (MPH)
                                        </Label>
                                    </Element>
                                )}
                                {sportType === "baseball" && (
                                    <Element
                                        data-trk-act="Sort by Positions"
                                        className={
                                            "td stat stat-positions " +
                                            getSortClass("positions")
                                        }
                                    >
                                        <Label type={Label.Type.Title5}>
                                            Positions
                                        </Label>
                                    </Element>
                                )}
                                {sportType === "soccer" && props.showStats && (
                                    <Element
                                        data-trk-act="Sort by Minutes"
                                        className={
                                            "td stat stat-minutes " +
                                            getSortClass("minutes_played")
                                        }
                                        onClick={(e) => {
                                            setSort("minutes_played")
                                        }}
                                    >
                                        <Label type={Label.Type.Title5}>
                                            Minutes Played
                                        </Label>
                                    </Element>
                                )}
                            </Element>
                        )}
                        {isPanoramicSensorless && (
                            <Element className="tr table-header">
                                <Label body color={Color.Black}>This game was processed without performance metrics.</Label>
                            </Element>
                        )}
                        {props.renderType === "sensorless" && (
                            <SensorlessResultsContent
                                className={sensorlessGameClass}
                                title={sensorlessGameTitle}
                            />
                        )}
                        <section className="side-panel-section">
                            <Element>{playerStatRows}</Element>
                        </section>
                        {showTraceTeamInfoCard && (
                            <TraceTeamInfoCard
                                renderAsRow={true}
                                onButtonClick={(e) =>
                                    handleShowTraceTeamDialog(e)
                                }
                            />
                        )}
                        {!isTraceFamilyGame &&
                            !isSensorless &&
                            !isPanoramicSensorless &&
                            !allDummies && (
                                <Element className="player-stats-legend">
                                    <Label>
                                        Key:
                                        <span className="top-stat top-stat-header">
                                            #1 in Game
                                        </span>
                                        <span className="above-avg-stat">
                                            Top 20%
                                        </span>
                                    </Label>
                                </Element>
                            )}
                    </Element>
                </Element>
            </Element>
        )
    }
}

const mapStateToProps = (state, props) => {
    let loading =
        state.team.loading || state.game.loading || state.player_stats.loading || state.player_stats_baseball.loading
    
    let selectedHalf = state.selectedHalf;
    let teamName = props.match.params.team_name
    let user = state.user.value
    let userDivisions = state.divisions.value
    let gameId = props.match.params.game_id
    let playlistId = props.match.params.playlist_id
    let eventNumber = parseInt(props.match.params.event_number, 10)
    let game = state.game.value
    let playerStats = []
    let playerStatsBaseball = state.player_stats_baseball.value ? state.player_stats_baseball.value : []
    let showStats = state.acl.value?.show_stats

    let hkey = "game"
    if (selectedHalf === 2) {
        hkey = "second_half"
    }
    if (selectedHalf === 1) {
        hkey = "first_half"
    }

    if (state.player_stats.value && state.player_stats.value.first_half) {
        playerStats = state.player_stats.value[hkey].filter(
            (ps) => ps.athlete !== undefined
        )

        if (game) {
            for (let player of game.home_team.players) {
                if (player.user && !player.user.first_name) {
                    player.user.first_name = ""
                }
                if (player.user && !player.user.last_name) {
                    player.user.last_name = ""
                }

                let found = false
                for (let stat of playerStats) {
                    if (player.trace_number === stat.trace_number) {
                        found = true
                        break
                    }
                }
                if (!found) {
                    let stat = {
                        accel_rank: 20,
                        acceleration: 0,
                        acceleration_formatted: "0.0",
                        acceleration_rank: 20,
                        athlete: player,
                        distance: 0,
                        distance_formatted: "0.0",
                        distance_rank: 20,
                        effort: 0,
                        effort_formatted: "0",
                        effort_rank: 20,
                        minutes_formatted: "0",
                        minutes_played: 0,
                        minutes_rank: 20,
                        speed_rank: 20,
                        stamina: 0,
                        stamina_formatted: "0%",
                        stamina_rank: 20,
                        top_speed: 0,
                        top_speed_formatted: "0.0",
                        top_speed_rank: 20,
                        trace_number: player.trace_number,
                        unknown:
                            !player ||
                            !player.user ||
                            player.user.last_name.includes("Tracer") ||
                            player.user.first_name.includes("Tracer"),
                        no_data: true,
                    }
                    playerStats.push(stat)
                }
            }
        }
    }

    return {
        renderType: game ? game.video_render_type : "none",
        loading,
        user,
        userDivisions,
        teamName,
        gameId,
        playlistId,
        eventNumber,
        playerStats,
        rev: state.rev,
        game: game,
        sportType: game ? game.sport_type : "",
        playerStatsBaseball: playerStatsBaseball,
        showStats
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        selectHalf: (selectedHalf) => {
            dispatch({
                type: "SET_FILTER",
                key: "selectedHalf",
                value: selectedHalf,
            })
        },
        setBaseballStats: (statsBaseball) => {
            dispatch({
                type: 'COMMON_SET',
                key: 'player_stats_baseball',
                value: statsBaseball
            })
        },
    }
}

const PlayersPanel = connect(
    mapStateToProps,
    mapDispatchToProps
)(PlayersPanelComponent)

export default PlayersPanel
